import SamButton from "../../../components/SamButton.tsx";
import React, {useEffect, useState} from "react";
import {useAppraiserApi} from "../../../context/ApiContext.tsx";
import {EppPartyInformation} from "../../../api/ApiTypes.ts";
import {PaymentsTabProps} from "./Payments.tsx";

export const EPP: React.FC<PaymentsTabProps> = ({appraiserInfo}) => {

    const [partyAccessUrl, setPartyAccessUrl] = useState<string | null>(null)
    const [ eppPartyInformation, setEppPartyInformation ] = useState<EppPartyInformation | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const appraiserApi = useAppraiserApi()

    useEffect(() => {

        getPartyAccessUrl();
        getEppPartyInformation();

    }, []);

    const getPartyAccessUrl = () => {

        if (!appraiserInfo?.id) {
            return;
        }

        setIsLoading(true);
        appraiserApi.getPartyAccessUrl(appraiserInfo.id).then(partyAccessUrl => {
            setPartyAccessUrl(partyAccessUrl);
        }).catch(console.log)
            .finally(() => setIsLoading(false));
    }

    const getEppPartyInformation = () => {

        if (!appraiserInfo?.id || !appraiserInfo.eppPartyData) {
            return;
        }

        setIsLoading(true);
        appraiserApi.getEppPartyInformation(appraiserInfo.id).then(eppPartyInformationResponse => {

            if (!eppPartyInformationResponse) {
                return;
            }

            setEppPartyInformation(eppPartyInformationResponse)
        }).catch(console.log)
            .finally(() => setIsLoading(false));

    }

    const onOnboardToEpp = async () => {

        if (!appraiserInfo?.id || appraiserInfo?.eppPartyData) {
            return null;
        }

        const onboardingToken: string = await appraiserApi.getOnboardingToken(appraiserInfo.id);
        if (!onboardingToken) {
            console.log("Could not get onboarding token for redirect");
            return;
        }

        const anchor = document.createElement("a");
        anchor.href = onboardingToken;
        anchor.target = "_blank";
        anchor.rel = "noopener noreferrer"; // Security best practice
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
    }

    return (
        <>
            {!appraiserInfo?.eppPartyData && (
                <>
                    <SamButton
                        onClick={onOnboardToEpp}
                        type="button"
                        disabled={!appraiserInfo?.readyForEppOnboarding || isLoading}
                    >
                         Onboard to EPP
                    </SamButton>
                    {isLoading &&
                        (<div className="text-blue-600" >connecting...</div>)
                    }
                </>
            )}

            {partyAccessUrl && appraiserInfo?.eppPartyData && (
             <SamButton
             >
                 <a
                     style={{ color: "white", textDecoration: "none" }}
                     className="button text-nowrap"
                     href={partyAccessUrl}
                     target="_blank"
                     rel="noopener noreferrer"
                 >
                     View EPP Profile
                 </a>
             </SamButton>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                {/* Left Section */}
                <div className="md:col-span-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Company Name */}
                        <div>
                            <label htmlFor="fasterPaymentsCompany" className="block font-medium">
                                Company Name
                            </label>
                            <input
                                id="fasterPaymentsCompany"
                                type="text"
                                value={eppPartyInformation?.companyName ?? "N/A"}
                                readOnly
                                className="w-full border border-gray-300 rounded-md p-2 bg-gray-100"
                            />
                        </div>

                        {/* Vendor Name */}
                        <div>
                            <label htmlFor="fasterPaymentsName" className="block font-medium">
                                Vendor Name
                            </label>
                            <input
                                id="fasterPaymentsName"
                                type="text"
                                value={eppPartyInformation?.vendorName ?? "N/A"}
                                readOnly
                                className="w-full border border-gray-300 rounded-md p-2 bg-gray-100"
                            />
                        </div>
                    </div>

                    {/* W9 Address (Only if linked) */}
                    {eppPartyInformation?.isLinked && (
                        <div>
                            <h1 className="mt-6 text-lg font-semibold">W9 Address</h1>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                                {/* Street Address */}
                                <div>
                                    <label htmlFor="streetAddress" className="block font-medium">
                                        Street Address
                                    </label>
                                    <input
                                        id="streetAddress"
                                        type="text"
                                        value={eppPartyInformation?.streetAddress ?? "N/A"}
                                        readOnly
                                        className="w-full border border-gray-300 rounded-md p-2 bg-gray-100"
                                    />
                                </div>

                                {/* State */}
                                <div>
                                    <label htmlFor="state" className="block font-medium">
                                        State
                                    </label>
                                    <input
                                        id="state"
                                        type="text"
                                        value={eppPartyInformation?.state ?? "N/A"}
                                        readOnly
                                        className="w-full border border-gray-300 rounded-md p-2 bg-gray-100"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                                {/* City */}
                                <div>
                                    <label htmlFor="city" className="block font-medium">
                                        City
                                    </label>
                                    <input
                                        id="city"
                                        type="text"
                                        value={eppPartyInformation?.city ?? "N/A"}
                                        readOnly
                                        className="w-full border border-gray-300 rounded-md p-2 bg-gray-100"
                                    />
                                </div>

                                {/* Zip Code */}
                                <div>
                                    <label htmlFor="zipCode" className="block font-medium">
                                        Zip Code
                                    </label>
                                    <input
                                        id="zipCode"
                                        type="text"
                                        value={eppPartyInformation?.zip ?? "N/A"}
                                        readOnly
                                        className="w-full border border-gray-300 rounded-md p-2 bg-gray-100"
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Right Section */}
                <div className="md:col-span-1">
                    <div className="ml-6 md:ml-12">
                        <label htmlFor="paymentStatus" className="block font-medium">
                            Payments Status
                        </label>
                        <input
                            id="paymentStatus"
                            type="text"
                            value={eppPartyInformation?.paymentStatus ?? "N/A"}
                            readOnly
                            className="w-full border border-gray-300 rounded-md p-2 bg-gray-100"
                        />
                    </div>
                </div>
            </div>
        </>
    );
};